export type AccessTokenSource =
  | 'github'
  | 'gitlab'
  | 'bitbucket'
  | 'azure-devops';

export type DetectedAccessToken = {
  source: AccessTokenSource;
  label: string;
};

const SOURCE_LABEL: Record<AccessTokenSource, string> = {
  github: 'GitHub token',
  gitlab: 'GitLab token',
  bitbucket: 'Bitbucket token',
  'azure-devops': 'Azure DevOps token',
};

const GITHUB_PREFIXES = [
  'github_pat_',
  'ghp_',
  'gho_',
  'ghu_',
  'ghs_',
  'ghr_',
] as const;

const GITLAB_PREFIXES = [
  'glpat-',
  'gldt-',
  'gloas-',
  'glrt-',
  'glptt-',
  'glcbt-',
  'glft-',
  'glffct-',
  'glimt-',
  'glagent-',
  'glsoat-',
] as const;

const BITBUCKET_PREFIXES = ['ATATT', 'ATCTT', 'ATAT', 'ATCT', 'ATBB'] as const;

const GITHUB_BODY = /^[A-Za-z0-9_]+$/;
const GITLAB_BODY = /^[A-Za-z0-9_-]+$/;
const BITBUCKET_BODY = /^[A-Za-z0-9_-]+$/;

function startsWithPrefix(value: string, prefix: string): boolean {
  return value.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase();
}

function bodyAfterPrefix(value: string, prefix: string): string {
  return value.slice(prefix.length);
}

function detectByPrefix(
  value: string,
  prefixes: readonly string[],
  bodyPattern: RegExp,
  minBodyLength: number,
): boolean {
  for (const prefix of prefixes) {
    if (!startsWithPrefix(value, prefix)) continue;
    const body = bodyAfterPrefix(value, prefix);
    if (body.length < minBodyLength) return false;
    if (!bodyPattern.test(body)) return false;
    return true;
  }
  return false;
}

function isAzureDevOpsPat(value: string): boolean {
  if (/^[A-Za-z0-9]{84}$/.test(value)) {
    const signature = value.slice(75, 79);
    const signatureAlt = value.slice(76, 80);
    return signature === 'AZDO' || signatureAlt === 'AZDO';
  }

  return /^[a-z2-7]{52}$/.test(value);
}

/**
 * Identify the SCM that issued a pasted access token.
 * Returns null until the value is long enough to match a known format.
 */
export function detectAccessToken(token: string): DetectedAccessToken | null {
  const value = token.trim();
  if (!value) return null;

  if (detectByPrefix(value, GITHUB_PREFIXES, GITHUB_BODY, 16)) {
    return { source: 'github', label: SOURCE_LABEL.github };
  }
  if (detectByPrefix(value, GITLAB_PREFIXES, GITLAB_BODY, 16)) {
    return { source: 'gitlab', label: SOURCE_LABEL.gitlab };
  }
  if (detectByPrefix(value, BITBUCKET_PREFIXES, BITBUCKET_BODY, 12)) {
    return { source: 'bitbucket', label: SOURCE_LABEL.bitbucket };
  }
  if (isAzureDevOpsPat(value)) {
    return { source: 'azure-devops', label: SOURCE_LABEL['azure-devops'] };
  }

  return null;
}

export function isConnectableAccessTokenSource(
  source: AccessTokenSource | null | undefined,
): source is 'github' | 'azure-devops' {
  return source === 'github' || source === 'azure-devops';
}

const ALL_PREFIXES = [
  ...GITHUB_PREFIXES,
  ...GITLAB_PREFIXES,
  ...BITBUCKET_PREFIXES,
] as const;

/** True while the value looks like a known token that is still incomplete. */
export function isPendingKnownAccessToken(token: string): boolean {
  const value = token.trim();
  if (!value) return false;
  if (ALL_PREFIXES.some(prefix => startsWithPrefix(value, prefix))) {
    return true;
  }
  return false;
}
